import { SKILL_NAMES } from 'js/src/agent/skills/skillNames';
import type { CodeoSkill } from '../types';
import { check } from '../skillUsage';

export const knockoutSilhouetteIconSkill: CodeoSkill = {
    name: SKILL_NAMES.knockoutSilhouetteIcon,
    kind: 'codeo',
    description:
        'Render a drawn symbol as a solid two-colour silhouette with its detail knocked out of the ' +
        'fill. One finishing style for an object whose shape you have already worked out — reach ' +
        'for it when a symbol needs to carry weight in the frame.',
    documentUsesSkill: check(
        (_script, markup) =>
            /fill-rule\s*=\s*["']evenodd/.test(markup) ||
            (/<mask\b/.test(markup) && /mask\s*=\s*["']url\(/.test(markup)) ||
            /clip-path\s*=\s*["']url\(/.test(markup),
    ),
    instructions: `# Knockout Silhouette Icon

**This skill is one rendering style, not the method for drawing objects.** \`${SKILL_NAMES.objectConstructionMethod}\` is what decides an object's silhouette, its canvas and proportions, and the marks that make it recognisable — load that to work out *what shape to draw*. Come here once you know the shape and want it rendered as a solid glyph. \`${SKILL_NAMES.svgDrawOn}\` draws an outline on and \`${SKILL_NAMES.svgIconEnrichment}\` animates one's internals; this is the solid-fill alternative to both.

The form is a solid silhouette with its detail **knocked out of the fill** rather than drawn on top of it. One colour of ink, and the ground showing through the holes. It reads at any size, survives compression, and carries weight in a frame the way a hairline outline never does — but it is the wrong choice over a gradient or a texture, and choosing it commits the whole graphic to the solid system rather than to line art.

A knockout glyph also fails in a specific way when it goes wrong: not as a wrong picture, but as an unreadable blob. The two sections below on *what a hole is* and *how much to draw* are the ones that prevent that.

## Two ways to cut a hole

**\`fill-rule="evenodd"\` on a single path**, when the glyph is static. Subpaths after the first punch through:

\`\`\`html
<svg viewBox="0 0 120 120" width="240" height="240" style="position: absolute; left: 200px; top: 240px;">
  <path fill-rule="evenodd" fill="var(--l5-color-1, #6b7280)" d="
    M4 60 A56 56 0 1 1 116 60 A56 56 0 1 1 4 60 Z
    M37 52 A7 7 0 1 1 51 52 A7 7 0 1 1 37 52 Z
    M69 52 A7 7 0 1 1 83 52 A7 7 0 1 1 69 52 Z
    M48 80 h24 a4 4 0 0 1 0 8 h-24 a4 4 0 0 1 0-8 Z"/>
</svg>
\`\`\`

**A \`<mask>\`**, when the knockouts have to animate. A mask is a paint surface, not a list of holes: **white keeps ink, black removes it**, and later shapes paint over earlier ones. A base rect covering the viewBox plus the hole shapes in black. This is the form to use whenever the detail should arrive separately from the silhouette:

\`\`\`html
<svg id="ksi-badge" viewBox="0 0 120 120" width="260" height="260"
     style="position: absolute; left: 510px; top: 220px;">
  <defs>
    <mask id="ksi-holes">
      <rect x="0" y="0" width="120" height="120" fill="#ffffff"/>
      <g id="ksi-hole-group" fill="#000000">
        <ellipse cx="44" cy="52" rx="7" ry="9"/>
        <ellipse cx="76" cy="52" rx="7" ry="9"/>
        <rect x="44" y="80" width="32" height="8" rx="4"/>
      </g>
    </mask>
  </defs>
  <circle cx="60" cy="60" r="56" fill="var(--l5-color-1, #6b7280)" mask="url(#ksi-holes)"/>
</svg>
\`\`\`

### A hole inside a hole is nothing

The mistake that turns a glyph into a blob: cutting a large shape, then cutting the detail *inside* it. Black painted onto black changes nothing — that region is already gone — so every detail line vanishes and what remains is one featureless void.

A globe knocked out of a shield, drawn wrong and right:

\`\`\`html
<!-- WRONG: the disc is cut, then the meridians are cut inside it. Nothing but a round hole. -->
<mask id="bad">
  <rect x="0" y="0" width="240" height="280" fill="#fff"/>
  <circle cx="120" cy="113" r="42" fill="#000"/>
  <path d="M78 113h84 M120 71c-18 18-18 66 0 84" stroke="#000" stroke-width="8" fill="none"/>
</mask>

<!-- RIGHT: cut the disc, then paint the meridians back in WHITE. They survive as ink. -->
<mask id="good">
  <rect x="0" y="0" width="240" height="280" fill="#fff"/>
  <circle cx="120" cy="113" r="42" fill="#000"/>
  <path d="M78 113h84 M120 71c-18 18-18 66 0 84" stroke="#fff" stroke-width="8" fill="none"/>
</mask>
\`\`\`

So: **detail that has to read inside a cut-out region must be white.** The ink is what's left behind, and the drawing you are actually making is the ink, not the holes. Before adding any shape to a mask, ask what colour the region under it already is — if it is already black, a black shape there is a no-op.

The same trap in \`fill-rule="evenodd"\` form: subpaths alternate, so a *third* nested subpath fills back in rather than cutting deeper. Two levels is all evenodd gives you cleanly; past that, use a mask where you can name each region's colour.

A tell you can check without rendering: **round \`stroke-linecap\` nubs poking out of an otherwise smooth hole.** That is a stroke that is invisible except where its caps overshoot the edge of the region it was drawn inside — i.e. this exact bug.

### Detail must meet the boundary it belongs to

Interior lines that stop short of the shape's edge leave a rim of unbroken ink all the way round, and the glyph reads as unfinished — the lines look like they are floating inside the form rather than crossing it. It is a small error that is instantly visible, because the eye tracks the gap, not the line.

The cause is always the same: extents eyeballed against a curve. A horizontal line inside a circle has to span the *chord* at that height, and the chord narrows faster the further out you go: a latitude at 40% of the radius above centre is already 8% shorter than the equator, and one at 70% is 29% shorter. Guessing splits the difference and gets every line wrong by a different amount.

**Clip instead of computing.** Draw the interior lines deliberately over-wide and clip them to the boundary. They then meet the edge exactly, by construction, at any radius — and if the shape's size changes later, nothing needs recalculating:

\`\`\`html
<defs>
  <clipPath id="ksi-disc"><circle cx="120" cy="118" r="57"/></clipPath>
  <mask id="ksi-globe">
    <rect x="0" y="0" width="240" height="280" fill="#fff"/>
    <circle cx="120" cy="118" r="57" fill="#000"/>
    <!-- over-wide on purpose; the clip decides where they end -->
    <g clip-path="url(#ksi-disc)" stroke="#fff" stroke-width="8" fill="none">
      <path d="M50 118h140 M50 94h140 M50 142h140"/>
      <path d="M120 61c-27 20-27 94 0 114 M120 61c27 20 27 94 0 114"/>
    </g>
  </mask>
</defs>
\`\`\`

If you do compute the extents instead, the chord half-width at height \`y\` in a circle of radius \`r\` centred \`(cx, cy)\` is \`Math.sqrt(r * r - (y - cy) ** 2)\`, so the line runs from \`cx - half\` to \`cx + half\`. And use \`stroke-linecap="butt"\` when computing — a round cap adds half the stroke width at each end and bulges past the edge you just landed on. Clipping doesn't have that problem, because it cuts the caps flush.

## Timeline code

The silhouette lands first as a solid shape, then the detail punches out of it. That order is worth the extra tween: the viewer reads a form arriving and then becoming specific, which is a beat, where a fully-detailed glyph fading in is just a fade.

Two things decide whether this reads as intended, and both are easy to get wrong: **where each element scales from**, and **what counts as one element**.

\`\`\`js
const tl = gsap.timeline({ paused: true });

const LAND = 0.4;
const CX = 60, CY = 60;   // the glyph's centre, in viewBox units

// EVERY scaled SVG element gets an explicit origin. Leaving it unset scales the element about the
// top-left corner of its own bbox, so each piece appears to grow in from up and to the left.
gsap.set('#ksi-badge', { svgOrigin: \`\${CX} \${CY}\` });

tl.fromTo('#ksi-badge', { scale: 0.72, opacity: 0 },
    { scale: 1, opacity: 1, duration: 0.5, ease: 'power3.out' }, LAND);

// The glyph's own detail belongs to the glyph: one target, one beat, from the glyph's centre.
gsap.set('#ksi-hole-group', { svgOrigin: \`\${CX} \${CY}\` });
tl.fromTo('#ksi-hole-group', { scale: 0 },
    { scale: 1, duration: 0.3, ease: 'power3.out' }, LAND + 0.42);

window.__timelines = { main: tl };
\`\`\`

### The origin is the point the motion is *about*

Not always the element's own centre. Marks that annotate a subject — ticks, rays, dashes pointing inward — should scale from **the subject's** centre, so they read as emanating from the thing they point at rather than each inflating in place:

\`\`\`
      |            each mark grows outward from the x,
   —  x  —         not from its own middle
\`\`\`

That needs them to be separate elements sharing one origin. Three marks bundled into a single \`<path>\` with three subpaths can only ever animate as one object, about one bbox — and that bbox's centre is nowhere near the subject.

\`\`\`html
<g id="ksi-marks">
  <rect class="ksi-mark" x="14" y="56" width="16" height="8" rx="4"/>
  <rect class="ksi-mark" x="90" y="56" width="16" height="8" rx="4"/>
  <rect class="ksi-mark" x="56" y="10" width="8" height="16" rx="4"/>
</g>
\`\`\`

\`\`\`js
// All three share the SUBJECT's centre, so a single staggered tween makes them radiate from it.
gsap.set('.ksi-mark', { svgOrigin: \`\${CX} \${CY}\` });
tl.fromTo('.ksi-mark', { scale: 0, opacity: 0 },
    { scale: 1, opacity: 1, duration: 0.34, ease: 'power3.out', stagger: 0.07 }, LAND + 0.7);
\`\`\`

### Separate the glyph's parts from its annotations

Put the glyph's own anatomy in one group and anything annotating it in another. A stagger across *every* child of one group treats a lock's body, shackle and keyhole as three independent arrivals, so the glyph assembles itself out of popping fragments instead of landing as one object — and it drags the annotation marks into the same queue. The glyph arrives as a unit; the marks are their own later beat.

## Construction

- **Build from primitives.** Circles, ellipses, rounded rects, and one or two arcs get you almost every glyph worth putting in a video frame. If you are past about six subpaths, you are drawing an illustration, and it will not read at frame scale.
- **Pick one knockout weight and hold it.** Choose a thickness — say 8 units in a 120 viewBox, ~6.7% of the glyph — and make every hole and every gap between holes at least that wide. This is what makes a set of hand-built glyphs look like a set.
- **Minimum hole size.** A knockout thinner than ~5% of the glyph's width closes up when the glyph is scaled down and mushes under H.264. If a detail needs to be finer than that, cut the detail.
- **The silhouette test.** Fill the glyph solid, with no holes at all. If it is still identifiable, the glyph works. If it needs its interior to be recognised, it is an illustration wearing an icon's clothes — simplify the outer shape until the silhouette carries it.
- **Two colours, and one of them is the ground.** The ink, and whatever shows through. A third colour inside a glyph is almost always the moment it stops reading as a symbol.
- **A family, not a one-off.** If the graphic needs three glyphs, build all three from the same primitives at the same knockout weight and the same corner radius. Since you are drawing each one from scratch, it is easy for the third to drift into a different weight or a different level of detail than the first — and three glyphs that don't belong to one another read worse than three plain circles.

## Adapt

- **Character glyph**: a circle head plus a tapered stem (a \`<path>\` with two straight sides converging) is a complete figure, and it multiplies well — a row, a ring, or a field of them costs almost nothing and reads as population. Pairs naturally with \`${SKILL_NAMES.dataAsMetaphor}\`'s countable-units encoding.
- **Badge**: the glyph inside a ring, or the glyph as the knockout *in* a disc. Set a label around it on an arc with \`${SKILL_NAMES.typeAsObject}\` rather than underneath it.
- **Object glyph**: knock the identifying detail out of a solid blob — the holes in a film reel, the slots in a coin, the bars of a window. The holes are what name the object, so spend the detail budget there.
- **Entrance alternatives**: scale from 0.7 with \`power3.out\` (as above, no overshoot); or draw the outer contour with \`${SKILL_NAMES.svgDrawOn}\` and fill it as the stroke closes; or reveal the whole glyph with a \`clip-path\` sweep.
- Once the glyph exists, \`${SKILL_NAMES.svgIconEnrichment}\` can animate its internals — knockouts are ordinary elements and tween like any other.

## Pitfalls

- **Without \`fill-rule="evenodd"\` the holes fill in.** The default is \`nonzero\`, which fills a subpath wound the same direction as the outer shape. Either set \`evenodd\`, or wind the inner subpaths the opposite way — \`evenodd\` is far easier to get right and to edit later.
- **A \`<mask>\` needs its white base rect covering the whole viewBox.** Omit it and the mask is black everywhere, which masks the entire glyph out — the symptom is a glyph that renders as nothing at all.
- **❗ Never paint black onto black in a mask.** Detail cut inside an already-cut region disappears and leaves a featureless void where a recognisable object was intended. If detail must read inside a hole, paint it white. See "A hole inside a hole is nothing".
- **Don't eyeball where an interior line meets a curve.** Lines stopping short of the edge leave a rim of unbroken ink and the glyph reads as unfinished. Clip them to the boundary, or compute the chord — see "Detail must meet the boundary it belongs to".
- **Look at the glyph rendered before you trust it.** A knockout that has gone wrong does not look like a wrong drawing, it looks like a blob — and the source reads perfectly plausibly, because every individual shape is where you meant it. Capture it and ask whether you could name the object without having written it.
- **Don't fake it by painting detail on top in the ground colour.** On a flat ground it looks identical, and it breaks the instant anything passes behind the glyph, the ground changes, or the glyph moves over another element — including a colour flood from \`${SKILL_NAMES.morphChainSpine}\`.
- **A knockout on a textured or gradient ground reads as a mistake** — the hole shows whatever is behind, and the eye reads a busy hole as dirt on the glyph. Knockouts want a flat ground; over a gradient field, put a flat scrim behind the glyph or use a solid glyph with no holes.
- **The only contrast that matters is glyph against ground**, because the knockouts *are* the ground. Check the ink against the surface behind it, not against itself.
- **❗ Never leave the transform origin unset on an SVG element you scale or rotate.** Measured on GSAP 3.12: with no origin set, the element transforms about the **top-left corner of its own bbox**, not its centre — so a group of pieces each grows in from up and to the left, which is the signature of this bug. Set \`svgOrigin\` (a point in viewBox units, the right choice when the origin is somewhere other than the element itself) or \`transformOrigin: '50% 50%'\` (the element's own centre). Do not rely on the default; there isn't a useful one.
- **A group of subpaths in one \`<path>\` is one element.** Convenient for a static glyph, fatal the moment any part of it needs its own timing or its own origin. If two marks should move differently, they are two elements.
- **Don't stagger a glyph's own anatomy.** A lock that assembles from a body, then a shackle, then a keyhole reads as fragments finding each other, not as a lock arriving. Stagger between *objects*, never within one.
- Keep no CSS \`transform\` on anything GSAP tweens here — the badge and the hole shapes are all animated.
- \`mask\` on very large glyphs plus a blur elsewhere in the composition is the one genuinely expensive combination; a handful of masked glyphs is fine, dozens are not.`,
};
